import * as React from "react";
import {useEffect} from "react";
import {backend} from "../../../../../xconvert-backend";
import {Accordion, Button, Icon, Tab, Table} from "semantic-ui-react";
import {LongRunningQueryByNs, Operation} from "../../../../../generated";
import {authentication} from "../../../../../authentication";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {CommandJsonModal} from "./CommandJsonModal";
import moment = require("moment");
import {TimedBarChart} from "../EtaQuality/TimedBarChart";
import {DatabaseCallsChart} from "./LongRunningQueriesChart";
import {ConfigContext} from "../../../context/ConfigContext";
import {clampTabIndex} from "../../../context/TabSession";


export function LongRunningQueries() {

    const context = React.useContext(ConfigContext);

    const [longRunningQueries, setLongRunningQueries] = React.useState<LongRunningQueryByNs[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const [secondsInPast, setSecondsInPast] = React.useState(3600);
    const [startDate, setStartDate] = React.useState(moment().subtract(1, 'days').toDate());
    const [openCommandJsonModal, setOpenCommandJsonModal] = React.useState(false);
    const [commandJson, setCommandJson] = React.useState("");

    useEffect(() => {
        let sip = calculateSecondsInPast(startDate);
        setSecondsInPast(sip);
        fetchLongRunningQueries(sip);
    }, []);

    function fetchLongRunningQueries(sip: number) {
        setLoading(true);
        let auth = backend.withTokenAuthHeader(authentication.token)

        backend.internalApi.getLongRunningQueries(sip, auth).then((response) => {
            console.log("LongRunningQueries", response)
            setLongRunningQueries(response.queries)
            setTotal(response.total)
        }).finally(() => {
            setLoading(false);
        })
    }

    const handleClick = (e, titleProps) => {
        const {index} = titleProps
        const newIndex = activeIndex === index ? -1 : index

        setActiveIndex(newIndex)
    }

    interface OperationRowProps {
        operation: Operation;
    }



    function drawAccordion() {

        const OperationRow: React.FC<OperationRowProps> = ({operation}) => {
            return (
                <Table.Row>
                    <Table.Cell><Button onClick={() => {
                        setCommandJson(JSON.stringify(operation, null, 4))
                        setOpenCommandJsonModal(true)
                    }
                    }>
                        raw
                    </Button></Table.Cell>
                    <Table.Cell>{operation.active ? 'Yes' : 'No'}</Table.Cell>
                    <Table.Cell>{operation.client}</Table.Cell>
                    <Table.Cell>
                        <Button onClick={() => {
                            setCommandJson(JSON.stringify(operation.command, null, 4))
                            setOpenCommandJsonModal(true)
                        }
                        }>
                            open Command
                        </Button>
                    </Table.Cell>
                    <Table.Cell>{operation.clientMetadata?.platform}</Table.Cell>
                    <Table.Cell>{operation.connectionId}</Table.Cell>
                    <Table.Cell>{operation.currentOpTime}</Table.Cell>
                    <Table.Cell>{operation.desc}</Table.Cell>
                    <Table.Cell>{operation.effectiveUsers?.map(user => user.user).join(', ')}</Table.Cell>
                    <Table.Cell>{operation.host}</Table.Cell>
                    <Table.Cell>{operation.msg}</Table.Cell>
                    <Table.Cell>{operation.ns}</Table.Cell>
                    <Table.Cell>{operation.numYields}</Table.Cell>
                    <Table.Cell>{operation.op}</Table.Cell>
                    <Table.Cell>{operation.opid}</Table.Cell>
                    <Table.Cell>{operation.secs_running}</Table.Cell>
                    <Table.Cell>{operation.threaded ? 'Yes' : 'No'}</Table.Cell>
                    <Table.Cell>{operation.type}</Table.Cell>
                    <Table.Cell>{operation.waitingForFlowControl ? 'Yes' : 'No'}</Table.Cell>
                    <Table.Cell>{operation.waitingForLatch?.captureName} - {operation.waitingForLatch?.timestamp?.toString()}</Table.Cell>
                    <Table.Cell>{operation.waitingForLock ? 'Yes' : 'No'}</Table.Cell>
                </Table.Row>
            );
        };
        return <div
            style={{
                padding: 10,
            }}>
            Total: {total}
            <Accordion styled fluid>
                {longRunningQueries.map((query, index) => {
                    return (
                        <>
                            <Accordion.Title
                                key={query.ns + "_" + index}
                                active={activeIndex === index}
                                index={index}
                                onClick={handleClick}
                            >
                                <Icon name='dropdown'/>
                                {query.ns} - {query.queries.length} queries
                            </Accordion.Title>
                            <Accordion.Content
                                active={activeIndex === index}
                            >
                                <Table celled>
                                    <Table.Header>
                                        <Table.HeaderCell>open</Table.HeaderCell>
                                        <Table.HeaderCell>Active</Table.HeaderCell>
                                        <Table.HeaderCell>Client</Table.HeaderCell>
                                        <Table.HeaderCell>Command</Table.HeaderCell>
                                        <Table.HeaderCell>Client Metadata Platform</Table.HeaderCell>
                                        <Table.HeaderCell>Connection ID</Table.HeaderCell>
                                        <Table.HeaderCell>Current Op Time</Table.HeaderCell>
                                        <Table.HeaderCell>Description</Table.HeaderCell>
                                        <Table.HeaderCell>Effective Users</Table.HeaderCell>
                                        <Table.HeaderCell>Host</Table.HeaderCell>
                                        <Table.HeaderCell>Message</Table.HeaderCell>
                                        <Table.HeaderCell>Namespace</Table.HeaderCell>
                                        <Table.HeaderCell>Number of Yields</Table.HeaderCell>
                                        <Table.HeaderCell>Operation</Table.HeaderCell>
                                        <Table.HeaderCell>Operation ID</Table.HeaderCell>
                                        <Table.HeaderCell>Seconds Running</Table.HeaderCell>
                                        <Table.HeaderCell>Threaded</Table.HeaderCell>
                                        <Table.HeaderCell>Type</Table.HeaderCell>
                                        <Table.HeaderCell>Waiting for Flow Control</Table.HeaderCell>
                                        <Table.HeaderCell>Waiting for Latch</Table.HeaderCell>
                                        <Table.HeaderCell>Waiting for Lock</Table.HeaderCell>
                                    </Table.Header>
                                    {query.queries.map((q, i) => {
                                        return OperationRow({operation: q})
                                    })}
                                </Table>
                            </Accordion.Content>
                        </>
                    )
                })}
            </Accordion>
        </div>
    }

    function drawSecondsInPastInput() {
        return <div
            style={{
                padding: 10,
            }}
        >
            From :
            <DatePicker
                selected={startDate}
                selectsStart
                startDate={startDate}
                showTimeSelect
                dateFormat="dd.MM.yyyy HH:mm:ss"
                //endDate={endDate}
                onChange={(date: Date) => handleDateChange(date)}
            /> to now. <a style={{fontSize: 10, padding: 10}}>(Seconds in past: {secondsInPast})</a>

            <Button onClick={() => fetchLongRunningQueries(secondsInPast)}><Icon name={"refresh"}/>Refresh</Button>
        </div>
    }

    const calculateSecondsInPast = (date: Date) => {
        const now = moment();
        return now.diff(moment(date), 'seconds');
    }

    const handleDateChange = (date: Date) => {
        setStartDate(date);
        setSecondsInPast(calculateSecondsInPast(date));
    }

    function drawGraph() {
        //return JSON.stringify(longRunningQueries, null, 4)
        return <DatabaseCallsChart data={longRunningQueries} />
    }

    function tabs() {
        let panes = [
            {
                menuItem: 'Table View', render: () => <Tab.Pane>
                    {drawAccordion()}
                </Tab.Pane>
            },
            {
                menuItem: 'graph View', render: () => <Tab.Pane>
                    {drawGraph()}
                </Tab.Pane>
            }
        ];

        return <Tab
            id={"LongRunningQueriesTab"}
            panes={panes}
            className={"flexTab"}
            defaultActiveIndex={clampTabIndex(context.tabSession.longRunningQueriesTab, panes)}
            onTabChange={(_, data) => {
                context.setSingleTabSession("longRunningQueriesTab", parseInt(data.activeIndex.toString()))
            }}
            menu={{secondary: true, pointing: true, style: {flexShrink: 0, borderBottom: '1px solid #ddd'}}}
        />
    }
    return <>
        {drawSecondsInPastInput()}
        {loading ? "Loading..." : tabs()}
        <CommandJsonModal
            open={openCommandJsonModal}
            onClose={() => setOpenCommandJsonModal(false)}
            commandJson={commandJson}
        />
    </>

}